import React from "react";
import { Box, Paper, Typography, Grid, Chip } from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";

const CustomToolbar = ({ year, permitNumber }) => (
  <GridToolbarContainer
    sx={{
      p: 2,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography variant="h6" sx={{ color: "primary.main", fontWeight: 600 }}>
      {year} Monthly Production Data - Permit #{permitNumber}
    </Typography>
    <GridToolbarExport
      slotProps={{
        button: {
          variant: "outlined",
          size: "small",
        },
      }}
    />
  </GridToolbarContainer>
);

// Utility function to convert gallons to acre-feet
const gallonsToAcreFeet = (gallons) => {
  return gallons / 325851; // 1 acre-foot = 325,851 gallons
};

// Utility function to format large numbers with commas
const formatNumber = (num) => {
  return Math.round(Number(num) || 0).toLocaleString();
};

// Get severity color based on curtailment percentage (matching CurrentDroughtAlert conventions)
const getCurtailmentSeverity = (curtailment) => {
  if (curtailment === 0) {
    return "success"; // green
  } else if (curtailment === 10 || curtailment === 20) {
    return "warning"; // yellow
  } else if (curtailment >= 30) {
    return "error"; // red
  }
  return "info"; // blue - default
};

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function DroughtProductionChart({
  permit,
  year,
  monthlyData,
  yearSummary,
}) {
  // Create a complete 12-month dataset with defaults for missing months
  const completeMonthlyData = Array.from({ length: 12 }, (_, index) => {
    const month = index + 1;
    const existingData = monthlyData.find((d) => d.rpt_month === month);

    if (existingData) {
      return existingData;
    }

    // Return default values for missing months
    return {
      rpt_month: month,
      gallons_pumped: 0,
      percent_of_year: 1.0 / 12.0, // Default to 8.33%
      stage_code: null,
      stage_label: "No Data",
      curtailment_pct: 0,
      suggested_allotment: permit.permit_gpy * (1.0 / 12.0),
      curtailed_allowance: permit.permit_gpy * (1.0 / 12.0),
      monthly_overage: 0,
      monthly_settlement_usd: 0,
    };
  });

  const columns = [
    {
      field: "month",
      headerName: "Month",
      width: 150,
      valueGetter: (value, row) => {
        if (row.isCumulative) {
          return row.rpt_month; // Use the quarter name directly
        }
        return monthNames[row.rpt_month - 1] || "Unknown";
      },
    },
    {
      field: "gallons_pumped",
      headerName: "Gallons Produced",
      width: 150,
      type: "number",
      valueFormatter: (value) => formatNumber(Number(value)),
    },
    {
      field: "percent_of_year",
      headerName: "Allotment Allocation",
      width: 180,
      type: "number",
      valueFormatter: (value) => `${(Number(value) * 100).toFixed(1)}%`,
    },
    {
      field: "stage_code",
      headerName: "Tier",
      width: 60,
      type: "number",
      renderCell: ({ row }) => {
        if (row.isCumulative || !row.stage_code) return "";
        return row.stage_code;
      },
    },
    {
      field: "stage_label",
      headerName: "Drought Stage",
      width: 160,
      renderCell: ({ row }) => {
        if (row.isCumulative || !row.stage_code) return "";
        const severity = getCurtailmentSeverity(Number(row.curtailment_pct));
        return (
          <Chip
            label={`${row.stage_label} - (${row.curtailment_pct}%)`}
            color={severity}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: "suggested_allotment",
      headerName: "Suggested Allotment",
      width: 200,
      type: "number",
      valueFormatter: (value) => formatNumber(Number(value)),
    },
    {
      field: "reduction_0",
      headerName: "0% Reduction",
      width: 150,
      type: "number",
      valueGetter: (value, row) => {
        if (!row.isCumulative) {
          const curtailment = Number(row.curtailment_pct);
          if (curtailment === 0) {
            return Number(row.curtailed_allowance);
          }
        }
        return null;
      },
      valueFormatter: (value) => {
        return value ? formatNumber(value) : "";
      },
      renderCell: ({ row }) => {
        if (!row.isCumulative) {
          const curtailment = Number(row.curtailment_pct);
          if (curtailment !== 0) return "";
          const value = Number(row.curtailed_allowance);
          return (
            <Box sx={{ color: "success.main", fontWeight: "bold" }}>
              {formatNumber(value)}
            </Box>
          );
        }
      },
    },
    {
      field: "reduction_10",
      headerName: "10% Reduction",
      width: 150,
      type: "number",
      valueGetter: (value, row) => {
        if (!row.isCumulative) {
          const curtailment = Number(row.curtailment_pct);
          if (curtailment === 10) {
            return Number(row.curtailed_allowance);
          }
        }
        return null;
      },
      valueFormatter: (value) => {
        return value ? formatNumber(value) : "";
      },
      renderCell: ({ row }) => {
        if (!row.isCumulative) {
          const curtailment = Number(row.curtailment_pct);
          if (curtailment !== 10) return "";
          const value = Number(row.curtailed_allowance);
          return (
            <Box sx={{ color: "warning.main", fontWeight: "bold" }}>
              {formatNumber(value)}
            </Box>
          );
        }
      },
    },
    {
      field: "reduction_20",
      headerName: "20% Reduction",
      width: 150,
      type: "number",
      valueGetter: (value, row) => {
        if (row.isCumulative) {
          // Show cumulative total in center column
          return Number(row.curtailed_allowance);
        }
        const curtailment = Number(row.curtailment_pct);
        if (curtailment === 20) {
          return Number(row.curtailed_allowance);
        }
        return null;
      },
      valueFormatter: (value) => {
        return value ? formatNumber(value) : "";
      },
      renderCell: ({ row }) => {
        if (row.isCumulative) {
          // Show cumulative total in center column
          const value = Number(row.curtailed_allowance);
          return formatNumber(value);
        }
        const curtailment = Number(row.curtailment_pct);
        if (curtailment !== 20) return "";
        const value = Number(row.curtailed_allowance);
        return (
          <Box sx={{ color: "warning.main", fontWeight: "bold" }}>
            {formatNumber(value)}
          </Box>
        );
      },
    },
    {
      field: "reduction_30",
      headerName: "30% Reduction",
      width: 150,
      type: "number",
      valueGetter: (value, row) => {
        if (!row.isCumulative) {
          const curtailment = Number(row.curtailment_pct);
          if (curtailment === 30) {
            return Number(row.curtailed_allowance);
          }
        }
        return null;
      },
      valueFormatter: (value) => {
        return value ? formatNumber(value) : "";
      },
      renderCell: ({ row }) => {
        if (!row.isCumulative) {
          const curtailment = Number(row.curtailment_pct);
          if (curtailment !== 30) return "";
          const value = Number(row.curtailed_allowance);
          return (
            <Box sx={{ color: "error.main", fontWeight: "bold" }}>
              {formatNumber(value)}
            </Box>
          );
        }
      },
    },
    {
      field: "reduction_40",
      headerName: "40% Reduction",
      width: 150,
      type: "number",
      valueGetter: (value, row) => {
        if (!row.isCumulative) {
          const curtailment = Number(row.curtailment_pct);
          if (curtailment === 40) {
            return Number(row.curtailed_allowance);
          }
        }
        return null;
      },
      valueFormatter: (value) => {
        return value ? formatNumber(value) : "";
      },
      renderCell: ({ row }) => {
        if (!row.isCumulative) {
          const curtailment = Number(row.curtailment_pct);
          if (curtailment !== 40) return "";
          const value = Number(row.curtailed_allowance);
          return (
            <Box sx={{ color: "error.main", fontWeight: "bold" }}>
              {formatNumber(value)}
            </Box>
          );
        }
      },
    },
    // Only show monthly overages and settlement if settlement mode is MONTHLY
    ...(yearSummary.settlement_mode === "MONTHLY"
      ? [
          {
            field: "monthly_overage",
            headerName: "Monthly Overages",
            width: 150,
            type: "number",
            valueFormatter: (value) => formatNumber(Number(value)),
          },
          {
            field: "monthly_settlement_usd",
            headerName: "Settlement ($)",
            width: 120,
            type: "number",
            valueFormatter: (value) => `$${Number(value).toFixed(0)}`,
          },
        ]
      : []),
  ];

  // Calculate quarterly cumulative data
  const calculateQuarterlyCumulative = (months, quarterName, isYTD = false) => {
    const totals = months.reduce(
      (acc, month) => {
        acc.gallons_pumped += Number(month.gallons_pumped) || 0;
        acc.percent_of_year += Number(month.percent_of_year) || 0;
        acc.suggested_allotment += Number(month.suggested_allotment) || 0;
        acc.curtailed_allowance += Number(month.curtailed_allowance) || 0;
        acc.monthly_overage += Number(month.monthly_overage) || 0;
        acc.monthly_settlement_usd += Number(month.monthly_settlement_usd) || 0;
        return acc;
      },
      {
        gallons_pumped: 0,
        percent_of_year: 0,
        suggested_allotment: 0,
        curtailed_allowance: 0,
        monthly_overage: 0,
        monthly_settlement_usd: 0,
      }
    );

    return {
      id: `cumulative-${quarterName.toLowerCase().replace(" ", "-")}`,
      rpt_month: quarterName,
      gallons_pumped: totals.gallons_pumped,
      percent_of_year: totals.percent_of_year,
      stage_code: null,
      stage_label: "Cumulative",
      curtailment_pct: null,
      suggested_allotment: totals.suggested_allotment,
      curtailed_allowance: totals.curtailed_allowance,
      monthly_overage: totals.monthly_overage,
      monthly_settlement_usd: totals.monthly_settlement_usd,
      isCumulative: true,
      isYTD,
    };
  };

  // Create rows with quarterly cumulative data
  const monthlyRows = completeMonthlyData.map((month, index) => ({
    id: index,
    ...month,
  }));

  const q1Data = completeMonthlyData.slice(0, 3);
  const q1Cumulative = calculateQuarterlyCumulative(q1Data, "1st Quarter");

  const q2Data = completeMonthlyData.slice(3, 6);
  const q2Cumulative = calculateQuarterlyCumulative(q2Data, "2nd Quarter");
  const q2YTD = calculateQuarterlyCumulative(
    completeMonthlyData.slice(0, 6),
    "YTD (Q1-Q2)",
    true
  );

  const q3Data = completeMonthlyData.slice(6, 9);
  const q3Cumulative = calculateQuarterlyCumulative(q3Data, "3rd Quarter");
  const q3YTD = calculateQuarterlyCumulative(
    completeMonthlyData.slice(0, 9),
    "YTD (Q1-Q3)",
    true
  );

  const q4Data = completeMonthlyData.slice(9, 12);
  const q4Cumulative = calculateQuarterlyCumulative(q4Data, "4th Quarter");
  const q4YTD = calculateQuarterlyCumulative(
    completeMonthlyData.slice(0, 12),
    "YTD (Full Year)",
    true
  );

  // Insert cumulative rows at appropriate positions
  const rows = [
    ...monthlyRows.slice(0, 3), // Jan, Feb, Mar
    q1Cumulative,
    ...monthlyRows.slice(3, 6), // Apr, May, Jun
    q2Cumulative,
    q2YTD,
    ...monthlyRows.slice(6, 9), // Jul, Aug, Sep
    q3Cumulative,
    q3YTD,
    ...monthlyRows.slice(9, 12), // Oct, Nov, Dec
    q4Cumulative,
    q4YTD,
  ];

  return (
    <Box>
      {/* Permit & Production Summary */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          border: "1px solid #e0e0e0",
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "primary.main", fontWeight: 600 }}
            >
              Permit Details
            </Typography>
            <Box sx={{ pl: 1 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1.5, display: "flex", alignItems: "center" }}
              >
                <Box
                  component="span"
                  sx={{ minWidth: 120, fontWeight: 500, color: "text.primary" }}
                >
                  Permit #:
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: "primary.main",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  }}
                >
                  {permit.permit_ndx}
                </Box>
              </Typography>
              {permit.permit_nickname && (
                <Typography
                  variant="body2"
                  sx={{ mb: 1.5, display: "flex", alignItems: "center" }}
                >
                  <Box
                    component="span"
                    sx={{
                      minWidth: 120,
                      fontWeight: 500,
                      color: "text.primary",
                    }}
                  >
                    Name:
                  </Box>
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    {permit.permit_nickname}
                  </Box>
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{ mb: 1.5, display: "flex", alignItems: "center" }}
              >
                <Box
                  component="span"
                  sx={{ minWidth: 120, fontWeight: 500, color: "text.primary" }}
                >
                  Report Year:
                </Box>
                <Box
                  component="span"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  {year}
                </Box>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <Box
                  component="span"
                  sx={{
                    minWidth: 120,
                    fontWeight: 500,
                    color: "text.primary",
                    display: "inline-block",
                  }}
                >
                  Annual Permit:
                </Box>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  {formatNumber(permit.permit_gpy)} gallons
                </Box>
                <Box
                  component="span"
                  sx={{ color: "text.disabled", fontSize: "0.85rem", ml: 1 }}
                >
                  ({gallonsToAcreFeet(permit.permit_gpy).toFixed(2)} acre-ft)
                </Box>
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 0, display: "flex", alignItems: "center" }}
              >
                <Box
                  component="span"
                  sx={{ minWidth: 120, fontWeight: 500, color: "text.primary" }}
                >
                  Settlement:
                </Box>
                <Box
                  component="span"
                  sx={{
                    color:
                      yearSummary.settlement_mode === "MONTHLY"
                        ? "warning.main"
                        : "success.main",
                    fontWeight: 500,
                    textTransform: "capitalize",
                  }}
                >
                  {yearSummary.settlement_mode || "Not specified"}
                </Box>
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={7}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "primary.main", fontWeight: 600 }}
            >
              Production Summary ({year})
            </Typography>
            <Box sx={{ pl: 1 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1.5, display: "flex", alignItems: "center" }}
              >
                <Box
                  component="span"
                  sx={{ minWidth: 180, fontWeight: 500, color: "text.primary" }}
                >
                  Total Production:
                </Box>
                <Box
                  component="span"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  {formatNumber(Number(yearSummary.total_gallons_produced))}{" "}
                  gallons
                </Box>
              </Typography>

              <Typography
                variant="body2"
                sx={{ mb: 1.5, display: "flex", alignItems: "center" }}
              >
                <Box
                  component="span"
                  sx={{ minWidth: 180, fontWeight: 500, color: "text.primary" }}
                >
                  % of Annual Permit:
                </Box>
                <Box
                  component="span"
                  sx={{
                    color:
                      (Number(yearSummary.total_gallons_produced) /
                        permit.permit_gpy) *
                        100 >
                      100
                        ? "error.main"
                        : "success.main",
                    fontWeight: 600,
                  }}
                >
                  {(
                    (Number(yearSummary.total_gallons_produced) /
                      permit.permit_gpy) *
                    100
                  ).toFixed(1)}
                  %
                </Box>
              </Typography>

              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <Box
                  component="span"
                  sx={{
                    minWidth: 180,
                    fontWeight: 500,
                    color: "text.primary",
                    display: "inline-block",
                  }}
                >
                  Drought Allowance:
                </Box>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  {formatNumber(Number(yearSummary.total_drought_curtailment))}{" "}
                  gallons
                </Box>
              </Typography>

              <Typography
                variant="body2"
                sx={{ mb: 1.5, display: "flex", alignItems: "center" }}
              >
                <Box
                  component="span"
                  sx={{ minWidth: 180, fontWeight: 500, color: "text.primary" }}
                >
                  % of Drought Allowance:
                </Box>
                <Box
                  component="span"
                  sx={{
                    color:
                      Number(yearSummary.total_drought_curtailment) > 0
                        ? (Number(yearSummary.total_gallons_produced) /
                            Number(yearSummary.total_drought_curtailment)) *
                            100 >
                          100
                          ? "error.main"
                          : "success.main"
                        : "text.disabled",
                    fontWeight: 600,
                  }}
                >
                  {Number(yearSummary.total_drought_curtailment) > 0
                    ? (
                        (Number(yearSummary.total_gallons_produced) /
                          Number(yearSummary.total_drought_curtailment)) *
                        100
                      ).toFixed(1)
                    : "0.0"}
                  %
                </Box>
              </Typography>

              <Typography
                variant="body2"
                sx={{ mb: 1.5, display: "flex", alignItems: "center" }}
              >
                <Box
                  component="span"
                  sx={{ minWidth: 180, fontWeight: 500, color: "text.primary" }}
                >
                  Excess Production:
                </Box>
                <Box
                  component="span"
                  sx={{
                    color:
                      Number(yearSummary.gallons_over) > 0
                        ? "error.main"
                        : "success.main",
                    fontWeight: 600,
                  }}
                >
                  {Number(yearSummary.gallons_over) > 0
                    ? `${formatNumber(
                        Number(yearSummary.gallons_over)
                      )} gallons`
                    : "None"}
                </Box>
              </Typography>

              <Typography
                variant="body2"
                sx={{ mb: 0, display: "flex", alignItems: "center" }}
              >
                <Box
                  component="span"
                  sx={{ minWidth: 180, fontWeight: 500, color: "text.primary" }}
                >
                  Settlement Fees:
                </Box>
                <Box
                  component="span"
                  sx={{
                    color:
                      Number(yearSummary.settlement_usd) > 0
                        ? "error.main"
                        : "success.main",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  }}
                >
                  {Number(yearSummary.settlement_usd) > 0
                    ? `$${Number(yearSummary.settlement_usd).toLocaleString()}`
                    : "$0"}
                </Box>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <Paper>
        <DataGrid
          rows={rows}
          density="compact"
          columns={columns}
          slots={{
            toolbar: (props) => (
              <CustomToolbar
                {...props}
                year={year}
                permitNumber={permit.permit_ndx}
              />
            ),
          }}
          disableRowSelectionOnClick
          getRowClassName={(params) => {
            if (params.row.isCumulative && params.row.isYTD) {
              return "ytd-row";
            } else if (params.row.isCumulative) {
              return "cumulative-row";
            }
            return "";
          }}
          sx={{
            "& .MuiDataGrid-cell": {
              fontSize: "0.875rem",
              borderRight: "1px solid #e0e0e0",
            },
            // Add border after the last reduction column (40% reduction)
            "& .MuiDataGrid-cell[data-field='reduction_40']": {
              borderRight: "1px solid #e0e0e0 !important",
            },
            "& .cumulative-row": {
              backgroundColor: "#DCE6F1",
              fontWeight: "bold",
              "& .MuiDataGrid-cell": {
                fontWeight: "bold",
                borderRight: "none !important",
              },
            },
            "& .ytd-row": {
              backgroundColor: "#E4DFEC",
              fontWeight: "bold",
              "& .MuiDataGrid-cell": {
                fontWeight: "bold",
                borderRight: "none !important",
              },
            },
          }}
        />
      </Paper>

      {/* Report Information */}
      <Paper
        sx={{
          p: 4,
          mt: 3,
          border: "1px solid #e9ecef",
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{ color: "primary.main", fontWeight: 600 }}
        >
          Understanding Drought Production Reports
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, lineHeight: 1.7 }}
        >
          This report analyzes monthly water production against drought
          curtailment allowances during periods of water restrictions. During
          drought conditions, water permits are subject to production reductions
          based on the declared drought stage, with each stage representing a
          percentage reduction from the normal annual allotment (0% = no
          restrictions, 10%-40% = increasing restrictions).
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, lineHeight: 1.7 }}
        >
          <strong>Table Structure:</strong> Monthly rows show individual month
          data, while <strong>blue quarterly rows</strong> display cumulative
          totals for Q1-Q4, and <strong>purple YTD rows</strong> show running
          year-to-date progress. The reduction columns (0%, 10%, 20%, 30%, 40%)
          display the allowable production amount only when that specific
          curtailment percentage is active for the month, helping visualize how
          drought restrictions impact water availability.
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, lineHeight: 1.7 }}
        >
          <strong>Settlement Modes:</strong> This permit uses{" "}
          <strong>{yearSummary.settlement_mode}</strong> settlement mode.
          {yearSummary.settlement_mode === "MONTHLY" ? (
            <>
              {" "}
              <strong>MONTHLY settlement</strong> charges fees for each
              individual month where production exceeds the drought-adjusted
              allowance, which is why monthly overage and settlement columns are
              visible in this table. This provides immediate accountability for
              excess production throughout the year. In contrast,{" "}
              <strong>ANNUAL settlement</strong> evaluates total yearly
              production against the cumulative drought allowances at year-end,
              meaning no monthly settlement fees are charged - only a final
              annual reconciliation occurs.
            </>
          ) : (
            <>
              {" "}
              <strong>ANNUAL settlement</strong> evaluates total yearly
              production against the cumulative drought allowances at year-end,
              meaning no monthly settlement fees are charged - only a final
              annual reconciliation occurs. Monthly overage and settlement
              columns are not shown for annual settlement permits. In contrast,{" "}
              <strong>MONTHLY settlement</strong> charges fees for each
              individual month where production exceeds allowances, providing
              immediate accountability throughout the year.
            </>
          )}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.7 }}
        >
          <strong>Key Metrics:</strong> The summary section shows total
          production as both raw gallons and percentages of the annual permit
          and drought allowance, helping assess compliance with water
          restrictions. Excess production and settlement fees are highlighted in
          red when applicable. Use the export button in the table toolbar to
          download this data for detailed analysis or reporting.
        </Typography>
      </Paper>
    </Box>
  );
}
